import React from 'react';

import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { TranslateFunction } from 'react-localize-redux';

import { OrderHistoryData } from '../../../core/state/order/order.types';
import { reorder } from '../../../utils/orderUtils';
import { urlUtils } from '../../../utils/urlUtils';
import {
  CollectionRestaurantSummary,
  DeliveryRestaurantSummary,
} from '../../../weborder/Api';
import { ToCurrency } from '../../../weborder/BasketControl';
import { HorizontalList } from '../../layouts/HorizontalList';
import { ButtonCard } from '../../molecules/ButtonCard';
import { ReorderCard } from '../../molecules/ReorderCard';
import { ReorderSkeleton } from './components/ReorderSkeleton';

const useStyles = makeStyles((theme: Theme) => ({
  reorderCard: {
    display: 'flex',
    height: 126,
    marginRight: theme.spacing(1.25),
    minWidth: 280,
  },
  reorderCardSingle: {
    marginRight: 0,
    width: '100%',
  },

  [theme.breakpoints.up('sm')]: {
    reorderCardSingle: { width: 280 },
  },
}));

type Props = {
  deliveryLocationId?: string;
  isLoading?: boolean;
  languageCode: string;
  listTitle: string;
  orders: OrderHistoryData[];
  restaurants?: CollectionRestaurantSummary[] | DeliveryRestaurantSummary[];
  setBasket: (payload: WebOrder.State.Basket) => void;
  setReorderOrderId: (orderId: number) => void;
  source: 'Store list' | 'Menu';
  translate: TranslateFunction;
};

export function Reorder(props: Props): JSX.Element {
  const {
    deliveryLocationId,
    isLoading,
    languageCode,
    listTitle,
    orders,
    restaurants = [],
    setBasket,
    setReorderOrderId,
    source,
    translate,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme?.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const navigateToPreviousOrdersScreen = (): void => {
    urlUtils.navigateToHash('/history');
  };

  const isOrderRestaurantInRestaurantsList = (
    prId: number,
    vrId: number,
  ): boolean =>
    restaurants.some(
      (restuarant: CollectionRestaurantSummary | DeliveryRestaurantSummary) =>
        (restuarant.PhysicalRestaurantId === prId &&
          restuarant.VirtualRestaurantId) === vrId,
    );

  const handleReorder = (order: OrderHistoryData): void => {
    const menuId = order.OrderHistoryItems[0].MenuId;
    const isReorderUnavailable =
      (source === 'Store list' &&
        !isOrderRestaurantInRestaurantsList(
          order.PhysicalRestaurantId,
          order.VirtualRestaurantId,
        )) ||
      !menuId;

    if (isReorderUnavailable) {
      enqueueSnackbar(translate('Reorder_Unavailable'), {
        variant: 'error',
      });
      return;
    }

    reorder(order, setBasket, setReorderOrderId, source, deliveryLocationId);
  };

  const getListItems = (): JSX.Element[] => {
    const reorderCardStyles = clsx(
      classes.reorderCard,
      orders?.length === 1 && classes.reorderCardSingle,
    );
    const items = orders?.map((order) => {
      const description = order.OrderHistoryItems?.map((x) => x.Name).join(
        ', ',
      );

      return (
        <div className={reorderCardStyles} key={order.OrderId}>
          <ReorderCard
            action={(): void => handleReorder(order)}
            actionText={translate('ReorderCard_Reorder') as string}
            description={description}
            price={ToCurrency(order.TotalAmount, order.Currency, languageCode)}
            title={order.RestaurantName}
          />
        </div>
      );
    });

    if (isMobileScreen) {
      items.push(
        <div className={classes.reorderCard}>
          <ButtonCard
            onClick={navigateToPreviousOrdersScreen}
            title={translate('Reorder_AllPreviousOrders') as string}
          />
        </div>,
      );
    }

    return items;
  };

  const actionText = !isMobileScreen
    ? (translate('ReorderCard_ShowAll') as string)
    : undefined;

  if (isLoading) {
    return <ReorderSkeleton />;
  }

  return (
    <HorizontalList
      action={navigateToPreviousOrdersScreen}
      actionText={actionText}
      dataFd="reorder"
      listItems={getListItems()}
      listName="reorder-list"
      title={listTitle}
    />
  );
}
